import React, { FC, Fragment, Suspense, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  ButtonAppearance,
  FormControl,
  Set,
  SetDirection,
  SetSpacing,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
  Text,
  TextArea,
  useModalState,
} from '@bechtel/gds';
import { ItrType, ReclaimMilestoneInput, ReclaimMilestonePayload } from '@bechtel/types';
import { Field, Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import { capitalize, get, some, startCase } from 'lodash';
import { object, string } from 'yup';
import { TitleValue, DrawerFooterButtonStyled, SuspenseLoader, ErrorBoundary } from '@bechtel/client-common';
import { RECLAIM_MILESTONE } from '@bechtel/client-gql';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { MilestoneError } from '../error';
import { t, Trans } from '@lingui/macro';
import { ReclaimMilestoneFieldprops, ReclaimMilestoneProps } from './types';
import { useLingui } from '@lingui/react';
import { clearCacheByKey, QueryKeys } from '~/services/cache';
import { ConfirmClaimAction } from '~/components/molecules/confirm';

interface FormikState {
  comments: ReclaimMilestoneInput['comments'];
  mediaMetadata?: ReclaimMilestoneInput['attachmentMetadata'];
}
const initialValues: FormikState = {
  comments: '',
  mediaMetadata: [],
};

const commentsHelperText = t`Please explain what was fixed`;

const reclaimMilestoneValidationSchema = object().shape({
  comments: string().required(commentsHelperText),
});

const ReclaimMilestoneField: FC<ReclaimMilestoneFieldprops> = ({ label, value, size, translateValues = false }) => {
  return (
    <Text weight="600" size={size} style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
      {label}:{' '}
      {value && (
        <Text size={size} weight="300">
          {translateValues ? startCase(capitalize(value)) : value}
        </Text>
      )}
    </Text>
  );
};

const ReclaimMilestone: FC<ReclaimMilestoneProps> = ({ onClose, milestone, refetchQueries, onActionCompleted }) => {
  const { i18n } = useLingui();
  const { tag: workPackageTag } = useWorkPackage();
  // TODO: Revisit the commodity name attribute, it may not be the commodity type name
  const { tag: commodityTag, typeName: commodityType } = useCommodity();

  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS;
  });

  //check if the connected welds(if there are any have already asked for ITR certificate)
  const isConnectedWeldsAlreadyAskedForCert =
    milestone.weldStatuses &&
    milestone.weldStatuses?.length > 0 &&
    some(milestone.weldStatuses, (weld) => weld?.itrRequired);

  const { visible: itrCertModalVisible, open: itrCertModalOpen, close: itrCertModalClose } = useModalState(false);
  const [formikValues, setFormikValues] = useState<FormikState>();
  const [formikActions, setFormikActions] = useState<FormikActions<FormikState>>();

  const [reclaimMilestone, { loading, data, error }] = useMutation<
    { reclaimMilestone: ReclaimMilestonePayload },
    { input: ReclaimMilestoneInput }
  >(RECLAIM_MILESTONE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      if (onActionCompleted) onActionCompleted();
    },
  });
  const milestoneError = get(data, 'reclaimMilestone.error') || error;
  const rejectionType = get(milestone, 'currentStatus.rejectionType.reason');
  const rejectionReason = get(milestone, 'currentStatus.comments');

  const onCertifyClick = async () => {
    onSubmit(formikValues, formikActions);
  };

  const onSubmit = async (values: FormikState, actions: FormikActions<FormikState>) => {
    const { data: reclaimData } = await reclaimMilestone({
      variables: {
        input: {
          commodityMilestoneId: milestone.id,
          comments: values.comments,
          attachmentMetadata: values.mediaMetadata,
          itrRequired: itrRequired && !isConnectedWeldsAlreadyAskedForCert,
        },
      },
      update: (cache, result) => {
        if (result.data && result.data.reclaimMilestone.commodity) {
          clearCacheByKey(cache, QueryKeys.EARNABLE_HOURS);
        }
      },
    });
    actions.resetForm({ values: initialValues });
    actions.setSubmitting(false);
    if (!get(reclaimData, 'reclaimMilestone.error')) onClose();
  };

  return (
    <>
      {milestoneError && <MilestoneError milestoneError={milestoneError} />}
      <SideSheet
        visible
        onScroll={(e) => {
          e.stopPropagation();
        }}
      >
        {itrCertModalVisible && (
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <ConfirmClaimAction
                onCertifyClick={onCertifyClick}
                isMutationInProgress={loading}
                close={itrCertModalClose}
              />
            </Suspense>
          </ErrorBoundary>
        )}

        <SideSheetHeader title={t`Re-Claim Milestone`}>
          <SideSheetClose onClick={onClose}></SideSheetClose>
        </SideSheetHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={reclaimMilestoneValidationSchema}
          onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
            if (itrRequired && !isConnectedWeldsAlreadyAskedForCert) {
              setFormikValues(values);
              setFormikActions(actions);
              itrCertModalOpen();
            } else {
              onSubmit(values, actions);
            }
          }}
        >
          {({ submitForm, errors, touched, isValid, dirty }: FormikProps<FormikState>) => {
            return (
              <Fragment>
                <SideSheetSubHeader>
                  <Set direction={SetDirection.COL} spacing={SetSpacing.COMPACT}>
                    <TitleValue title={t`Work Package`} value={workPackageTag} size="500" />
                    <TitleValue title={commodityType} value={commodityTag} size="500"></TitleValue>
                    <TitleValue title={t`Milestone`} value={milestone.name} size="500"></TitleValue>
                  </Set>
                </SideSheetSubHeader>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <Box mt={{ sm: '600' }} ml={{ sm: '500' }} flexDirection={{ sm: 'column' }} mr={{ sm: '400' }}>
                      <Box mb={{ sm: '300' }}>
                        <ReclaimMilestoneField
                          label={t`Rejection Type`}
                          value={rejectionType}
                          size="400"
                          translateValues={true}
                        ></ReclaimMilestoneField>
                      </Box>
                      <ReclaimMilestoneField
                        label={t`Reason For Rejection`}
                        value={rejectionReason}
                        size="400"
                      ></ReclaimMilestoneField>
                    </Box>
                    <Box px={{ sm: '500' }} py={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                      <Box>
                        <FormControl
                          label={t`Comments (Required)`}
                          css={`
                            flex-grow: 1;
                          `}
                          error={touched.comments && errors.comments ? i18n._(errors.comments) : ''}
                        >
                          <Field name="comments">
                            {({ field }) => {
                              return (
                                <TextArea
                                  maxLength={2000}
                                  {...field}
                                  rows="4"
                                  placeholder={t`Add a comment`}
                                ></TextArea>
                              );
                            }}
                          </Field>
                          {!(errors && errors.comments) && (
                            <Box ml={{ sm: '500' }} mt={{ sm: '200' }}>
                              <Text size="400" weight="300" color="grey.1000">
                                {i18n._(commentsHelperText)}
                              </Text>
                            </Box>
                          )}
                        </FormControl>
                      </Box>
                    </Box>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={onClose}
                        disabled={loading}
                      >
                        <Trans> Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={submitForm}
                        disabled={!dirty || !isValid || loading}
                      >
                        {loading && <Spinner />}
                        {!loading && <Trans>Confirm</Trans>}
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </Fragment>
            );
          }}
        </Formik>
      </SideSheet>
    </>
  );
};

export default ReclaimMilestone;
